<script lang="ts" setup>
import {defineAsyncComponent} from 'vue';
import Icon from '/src/components/layout/Icon.vue';
import NavbarAuthSection from '/src/components/layout/NavbarAuthSection.vue';
import {NAVBAR_TAGLINE, useNavbar} from '/src/components/layout/Navbar';
import ChunkErrorFallback from '/src/components/layout/ChunkErrorFallback.vue';
import {loadChunkWithRecovery} from '/src/core/chunk-recovery';

const CancellationModal = defineAsyncComponent(() =>
    loadChunkWithRecovery(() => import('/src/components/layout/CancellationModal.vue'), {
        fallback: {default: ChunkErrorFallback},
        storage: sessionStorage,
        reload: () => window.location.reload(),
        logger: console,
    }),
);
const ReferralDialog = defineAsyncComponent(() => import('/src/components/referrals/ReferralDialog.vue'));

const {
    auth,
    subscription,
    isRedirecting,
    isCancellationModalOpen,
    isReferralDialogOpen,
    mainLink,
    userAvatarText,
    isSubscriptionActive,
    referralButtonText,
    logOut,
    openCancellationModal,
    openManageSubscriptionModal,
    openReferralDialog,
    trackEvent,
} = useNavbar();
</script>
<template>
    <div class="navbar flex-col justify-between py-0 md:flex-row">
        <div class="navbar-start w-max md:flex-1">
            <router-link
                :to="mainLink"
                class="flex min-h-12 flex-row items-center sm:min-h-16 md:pl-2">
                <div class="w-8">
                    <img
                        class="lopaka-logo"
                        src="/assets/lopaka-logo.svg"
                        width="32"
                        height="32"
                        alt="Lopaka Logo" />
                </div>
                <div class="font-heading pl-2 text-xl text-white">
                    <a
                        href="/"
                        class="text-white">
                        Lopaka
                    </a>
                </div>
            </router-link>
            <div
                v-if="!auth"
                class="text-primary hidden pl-4 xl:block">
                <span>{{ NAVBAR_TAGLINE }}</span>
            </div>
        </div>
        <div
            v-if="!auth"
            class="navbar-center">
            <ul class="menu menu-horizontal flex-row items-center text-base lg:-ml-[10%]">
                <li v-if="auth">
                    <router-link
                        class="link link-primary"
                        to="/projects">
                        Projects
                    </router-link>
                </li>
                <li>
                    <router-link
                        class="link link-primary"
                        to="/gallery">
                        Gallery
                    </router-link>
                </li>
                <li>
                    <a
                        class="link link-primary"
                        href="/pages/"
                        target="_blank">
                        Journal
                    </a>
                </li>
                <li>
                    <a
                        class="link link-primary"
                        href="/pages/docs/"
                        target="_blank">
                        Docs
                    </a>
                </li>
                <li>
                    <a
                        class="link link-primary"
                        :href="`https://lopaka.fibery.io/@public/forms/cot3nHiE?email=${auth?.user?.email ?? ''}`"
                        target="_blank">
                        Contact Us
                    </a>
                </li>
                <!-- <li>
                <a
                    @click="logEvent('button_sponsor')"
                    href="https://buymeacoffee.com/milin"
                    class="btn btn-sm btn-info"
                    title="Every donation matters"
                >
                    ❤️&nbsp;&nbsp;Sponsor
                </a>
            </li> -->
            </ul>
        </div>
        <div class="navbar-end flex w-max flex-1 flex-row gap-2">
            <div class="flex flex-row items-center md:pl-2">
                <template v-if="!auth">
                    <div
                        class="tooltip tooltip-bottom"
                        data-tip="Source Code">
                        <a
                            class="link text-base-content/60 flex min-h-12 min-w-10 items-center justify-center"
                            href="https://github.com/sbrin/lopaka">
                            <Icon type="github" />
                        </a>
                    </div>
                    <div
                        class="tooltip tooltip-bottom"
                        data-tip="Follow Us on Twitter">
                        <a
                            class="link text-base-content/60 flex min-h-12 min-w-10 items-center justify-center"
                            href="https://twitter.com/lopaka_app">
                            <Icon type="twitter" />
                        </a>
                    </div>
                    <div
                        class="tooltip tooltip-bottom"
                        data-tip="Follow Us on Instagram">
                        <a
                            class="link text-base-content/60 flex min-h-12 min-w-10 items-center justify-center"
                            href="https://www.instagram.com/lopaka_app">
                            <Icon type="instagram" />
                        </a>
                    </div>
                    <div
                        class="tooltip tooltip-bottom"
                        data-tip="Join Lopaka Hub on Discord">
                        <a
                            class="link text-base-content/60 flex min-h-12 min-w-10 items-center justify-center"
                            href="https://discord.com/invite/7NhZG9AANb">
                            <Icon type="discord" />
                        </a>
                    </div>
                </template>
                <button
                    v-else
                    type="button"
                    data-testid="navbar-invite-friend"
                    class="btn btn-accent btn-ghost btn-sm whitespace-nowrap"
                    @click="openReferralDialog">
                    🎁 &nbsp; {{ referralButtonText }}
                </button>
            </div>
            <NavbarAuthSection
                :auth="auth"
                :is-subscription-active="isSubscriptionActive"
                :plan="subscription?.plan"
                :user-avatar-text="userAvatarText"
                :product-name="subscription?.product_name"
                :subscription-id="subscription?.sub_id"
                :subscription-ends-at="subscription?.ends_at"
                @open-manage-subscription-modal="openManageSubscriptionModal"
                @open-cancellation-modal="openCancellationModal"
                @logout="logOut"
                @sign-in-click="trackEvent('button_sign_in', 'navbar')" />
        </div>
    </div>
    <CancellationModal
        v-model="isCancellationModalOpen"
        :subscription="subscription" />
    <ReferralDialog v-model="isReferralDialogOpen" />
    <dialog
        v-if="isRedirecting"
        class="modal modal-open backdrop-blur-md">
        <div class="modal-box border-base-300 relative flex flex-col items-center gap-4 border p-8">
            <button
                class="btn btn-sm btn-circle btn-ghost absolute top-2 right-2"
                @click="isRedirecting = false">
                ✕
            </button>
            <span class="loading loading-spinner loading-lg text-primary"></span>
            <p class="text-base-content/70 text-sm">Redirecting…</p>
        </div>
    </dialog>
</template>
